import { Answer, Question, QuestionAnswered, SearchAnswered, SurveyAnswered } from "models";
import { Utility } from "./utility";
import { environment } from "src/environments/environment";
import { BrowserConfig } from "src/_services";

export class SearchUtility {
    /**
     * Check if id is a question id. E.G 1.10.20
     * @param id id to test
     * @returns true | false
     */
    static isId(id: string): boolean { return (/^[0-9]{1,}.[0-9]{1,}.[0-9]{1,}$/gm).test(id); };


    /**
     * Extract all survey id from a search like object
     * @param search with the surver to export
     * @returns [...survey ids]
     */
    static extractSurveyIds = (search: SearchAnswered) => { return Object.keys(search).filter(fil => !isNaN(+fil)) };

    static extractQuestionIds(search: SearchAnswered) {
        const tr: string[] = [];
        SearchUtility.extractSurveyIds(search).forEach(keySurv => {
            Object.keys(search[keySurv]).forEach(key => {
                if (SearchUtility.isId(key)) tr.push(key)
            })
        })
        return tr;
    }

    /**
     * Split the question id in sub id
     * @param questionId question id to split
     * @returns The id of survey, question and subquestion
     */
    static getIdDescription = (questionId: string) => {
        const spl = questionId.split('.');
        return {
            survey: +spl[0],
            question: +spl[1],
            subquestion: +spl[2]
        }
    };


    /** Return all the question ids from a survey */
    static questionIdsInSurvey(s: SurveyAnswered) {
        return Object.keys(s).filter(fil => SearchUtility.isId(fil)).flat()
    }


    static getQuestionById(search: SearchAnswered, id: string): QuestionAnswered {
        const sid = SearchUtility.getIdDescription(id);
        return search[sid.survey.toString()][id] as QuestionAnswered;
    }
    /**
     * extrac all the questions in search and return them as array
     */
    static extractAnsweredQuestions = (search: SearchAnswered): QuestionAnswered[] => {
        const tr: QuestionAnswered[] = [];
        SearchUtility.extractQuestionIds(search).sort((a, b) => SearchUtility.compareIds(a, b)).forEach(key => {
            tr.push(SearchUtility.getQuestionById(search, key))
        })
        return tr;
    }

    /**
     * Adde a question answerd to search
     * @param search search to modify
     * @param qa question answered to edit
     */
    static setSearchValue(search: SearchAnswered, qa: QuestionAnswered) {
        const sid = SearchUtility.getIdDescription(qa.id)
        if (!search[sid.survey.toString()])
            search[sid.survey.toString()] = {} as any;
        search[sid.survey.toString()][qa.id] = qa;
    }

    /**
     * Compare two question id
     * @param a question id
     * @param b question id
     * @returns x>0 if a after b | x=0 if a == b | x<0 if a before b
     */
    static compareIds(a: string, b: string): number {
        const version1Parts = a.split(".");
        const version2Parts = b.split(".");

        for (let i = 0; i < version1Parts.length; i++) {
            if (version1Parts[i] !== version2Parts[i]) {
                return Number(version1Parts[i]) - Number(version2Parts[i]);
            }
        }

        return 0;
    }

    /**
       * Return how much b filtered in relation of a. B is after A
       * @param a 
       * @param b 
       */
    static howFiltrant(a: QuestionAnswered, b: QuestionAnswered) {
        const A = a.countPolicies;
        const B = b.countPolicies;

        const deltaAss = Math.abs(B - A);
        const deltaPerc = (B / A) * 100;
        return deltaAss * (1 + deltaPerc);

    }

    /**
     * Return the most filtrant question given in a search. The algorithm has been decided by the client
     * @param search Search where to find the question
     * @returns moreFiltrantQuestion: QuestionAnswered
     */
    static getMoreFiltrantQuestionAnswered(search: SearchAnswered) {

        const qs = SearchUtility.extractAnsweredQuestions(search);
        const pairs = Utility.toPairs(qs);

        let maxSaved: number = pairs[0][0].countPolicies
        let moreFiltrant: QuestionAnswered = pairs[0][0];

        for (let pair of pairs) {
            const max = SearchUtility.howFiltrant(pair[0], pair[1]);
            if (!maxSaved || max > maxSaved) {
                maxSaved = max;
                moreFiltrant = pair[1];
            }
        }

        return moreFiltrant;
    }
    /**
     * Return the less filtrant question given in a search. The algorithm has been decided by the client
     * @param search Search where to find the question
     * @returns lessFiltrantQuestion: QuestionAnswered
     */
    static getLessFiltrantQuestionAnswered(search: SearchAnswered) {

        const qs = SearchUtility.extractAnsweredQuestions(search);
        const pairs = Utility.toPairs(qs);

        let minSaved: number = pairs[0][0].countPolicies
        let lessFiltrant: QuestionAnswered = pairs[0][0];

        for (let pair of pairs) {
            const min = SearchUtility.howFiltrant(pair[0], pair[1]);
            if (!minSaved || min < minSaved) {
                minSaved = min;
                lessFiltrant = pair[1];
            }
        }

        return lessFiltrant;
    }

    static getNonFilteringQuestions(questionsAnswered: QuestionAnswered[]): QuestionAnswered[] {
        let nonFilteringQuestions: QuestionAnswered[] = [];
        const pairs = Utility.toPairs(questionsAnswered);

        for (let pair of pairs) {
            const delta = pair[1].countPolicies - pair[0].countPolicies;
            if(delta == 0) { nonFilteringQuestions.push(pair[1]) }
        }

        return nonFilteringQuestions
    }

    static getMoreFilteringQuestions(questionsAnswered: QuestionAnswered[]): QuestionAnswered[] {
        let moreFilteringQuestions: QuestionAnswered[] = [];
        const pairs = Utility.toPairs(questionsAnswered);

        for (let pair of pairs) {
            const delta = Math.abs(pair[1].countPolicies - pair[0].countPolicies);
            if(delta > (BrowserConfig.nMax / 2)) { moreFilteringQuestions.push(pair[1]) }
        }

        return moreFilteringQuestions
    }

    static isQuestionAnswered(q: Question): boolean {
        return q.answers.findIndex(x => x.isSelected) > -1
    }

    static getAnswerOfQuestion(q: Question): Answer {
        return q.answers.find(x => x.isSelected)
    }
}